import type { FieldDefinition, Json, OperatorArity } from "@estelle/domain";

import { DatePicker } from "@/components/dates/DatePicker";
import { DateRangePicker } from "@/components/dates/DateRangePicker";
import {
  DateTimePicker,
  DateTimeRangePicker,
} from "@/components/dates/DateTimePicker";
import { TokenInput } from "@/components/filters/TokenInput";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * The HTML input type a field's values are entered with. Dates and datetimes
 * are not here: they have their own pickers, below.
 */
function inputTypeFor(field: FieldDefinition): string {
  return field.type === "number" || field.type === "money" ? "number" : "text";
}

/**
 * Values arrive as `Json` because that is what survives a URL and a `jsonb`
 * column. An object or array reaching an input would render as
 * "[object Object]", so anything that is not a scalar becomes an empty field
 * — visibly unset, rather than visibly nonsense.
 */
function asString(value: Json | undefined): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function asArray(value: Json | undefined): string[] {
  return Array.isArray(value) ? value.map((item) => asString(item)) : [];
}

/**
 * The value side of a condition.
 *
 * Driven by the operator's arity and the field's type, both of which come from
 * the registry in `@estelle/domain`. This component has no table of special
 * cases — `between` renders two inputs because its arity says `two`, not
 * because it is named `between`.
 */
export function ValueInput({
  field,
  arity,
  value,
  onChange,
}: {
  field: FieldDefinition;
  arity: OperatorArity;
  value: Json | undefined;
  onChange: (value: Json | undefined) => void;
}) {
  if (arity === "none") return null;

  if (arity === "many") {
    if (field.options) {
      const selected = new Set(asArray(value));
      return (
        <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border p-2">
          {field.options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm"
            >
              <input
                type="checkbox"
                checked={selected.has(option.value)}
                onChange={(event) => {
                  const next = new Set(selected);
                  if (event.target.checked) next.add(option.value);
                  else next.delete(option.value);
                  onChange([...next]);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    }

    return (
      <TokenInput
        values={asArray(value)}
        onChange={(values) => {
          onChange(values);
        }}
      />
    );
  }

  if (arity === "two") {
    const pair = asArray(value);
    if (field.type === "date") {
      return (
        <DateRangePicker
          className="h-8 w-full"
          from={pair[0] ?? ""}
          to={pair[1] ?? ""}
          onChange={(range) => {
            onChange([range.from, range.to]);
          }}
        />
      );
    }
    if (field.type === "datetime") {
      return (
        <DateTimeRangePicker
          className="h-8 w-full"
          from={pair[0] ?? ""}
          to={pair[1] ?? ""}
          onChange={(range) => {
            onChange([range.from, range.to]);
          }}
        />
      );
    }
    return (
      <div className="flex items-center gap-2">
        <Input
          type={inputTypeFor(field)}
          className="h-8"
          value={pair[0] ?? ""}
          onChange={(event) => {
            onChange([event.target.value, pair[1] ?? ""]);
          }}
        />
        <span className="text-muted-foreground text-xs">and</span>
        <Input
          type={inputTypeFor(field)}
          className="h-8"
          value={pair[1] ?? ""}
          onChange={(event) => {
            onChange([pair[0] ?? "", event.target.value]);
          }}
        />
      </div>
    );
  }

  // A tag field with known options — a product's collections (#308) — picks
  // from them the same way an enum does. "Has Luxe Watches" typed by hand
  // only matches if the title is spelled exactly; a free-form tag, which
  // declares no options, still gets the text input below.
  if ((field.type === "enum" || field.type === "tag") && field.options) {
    return (
      <Select
        value={asString(value)}
        onValueChange={(next) => {
          onChange(next);
        }}
      >
        <SelectTrigger className="h-8 w-full">
          <SelectValue placeholder="Choose" />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (field.type === "date") {
    return (
      <DatePicker
        className="h-8"
        value={asString(value)}
        onChange={(next) => {
          onChange(next);
        }}
      />
    );
  }

  if (field.type === "datetime") {
    return (
      <DateTimePicker
        className="h-8"
        value={asString(value)}
        onChange={(next) => {
          onChange(next);
        }}
      />
    );
  }

  return (
    <Input
      type={inputTypeFor(field)}
      className="h-8"
      value={asString(value)}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}
